import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { customerService } from '../../../services/customerService';
import ContractorsVendorsSelector from '../../project-management/components/ContractorsVendorsSelector';
import { getBidContractors, setBidContractors } from '../../../services/contractorVendorLinkService';
import { toDateInputValue } from '../../../utils/dateUtils';

// Quick-create customer modal (minimal info)
const QuickCreateCustomerModal = ({ isOpen, onClose, onCreated }) => {
  const [formData, setFormData] = useState({ companyName: '', contactPerson: '', email: '', phone: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({ companyName: '', contactPerson: '', email: '', phone: '' });
      setErrors({});
      setSaveError(null);
    }
  }, [isOpen]);

  const validate = () => {
    const newErrors = {};
    if (!formData?.companyName?.trim()) newErrors.companyName = 'Company name is required';
    if (!formData?.contactPerson?.trim()) newErrors.contactPerson = 'Contact person is required';
    if (!formData?.email?.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) newErrors.email = 'Enter a valid email';
    if (!formData?.phone?.trim()) newErrors.phone = 'Phone is required';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    setSaveError(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { data, error } = await customerService?.create(formData);
      if (error) throw new Error(error?.message || 'Failed to create customer');
      onCreated(data);
      onClose();
    } catch (err) {
      setSaveError(err?.message || 'Failed to create customer');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-black/60">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="UserPlus" size={20} className="text-accent" />
            <h3 className="text-lg font-semibold text-foreground">New Customer</h3>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {saveError && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
              <Icon name="AlertCircle" size={16} className="shrink-0" />
              <span>{saveError}</span>
            </div>
          )}
          <Input
            label="Company Name"
            required
            value={formData?.companyName}
            onChange={(e) => handleChange('companyName', e?.target?.value)}
            error={errors?.companyName}
            placeholder="Enter company name"
          />
          <Input
            label="Contact Person"
            required
            value={formData?.contactPerson}
            onChange={(e) => handleChange('contactPerson', e?.target?.value)}
            error={errors?.contactPerson}
            placeholder="Enter contact name"
          />
          <Input
            label="Email"
            type="email"
            required
            value={formData?.email}
            onChange={(e) => handleChange('email', e?.target?.value)}
            error={errors?.email}
            placeholder="email@example.com"
          />
          <Input
            label="Phone"
            type="tel"
            required
            value={formData?.phone}
            onChange={(e) => handleChange('phone', e?.target?.value)}
            error={errors?.phone}
            placeholder="555-123-4567"
          />
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="outline" type="button" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="default" type="submit" disabled={saving} iconName={saving ? undefined : "UserPlus"} iconPosition="left" iconSize={16}>
              {saving ? 'Creating...' : 'Create Customer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BidFormModal = ({ isOpen, onClose, onSubmit, editBid = null }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    projectName: '',
    totalAmount: '',
    laborAmount: '',
    equipmentRental: '',
    suppliesMaterials: '',
    projectStartDate: '',
    projectEndDate: '',
    projectLocation: '',
    description: '',
    documentationLink: '',
    status: 'Draft'
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [customerError, setCustomerError] = useState(null);
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);

  // Contractor/Vendor selection
  const [selectedSCIds, setSelectedSCIds] = useState([]);
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);

  // Fetch customers from database
  const fetchCustomers = async () => {
    setLoadingCustomers(true);
    setCustomerError(null);
    try {
      const { data, error } = await customerService?.getAll();
      if (error) throw error;
      setCustomers(data || []);
    } catch (err) {
      console.error('Error fetching customers:', err?.message);
      setCustomerError('Failed to load customers');
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCustomers();
    }
  }, [isOpen]);

  // Convert customers to select options
  const customerOptions = [...(customers || [])]
    ?.sort((a, b) => (a?.companyName || '')?.localeCompare(b?.companyName || ''))
    ?.map(customer => ({ value: customer?.companyName, label: customer?.companyName }));

  useEffect(() => {
    if (editBid) {
      setFormData({
        customerName: editBid?.customerName || '',
        projectName: editBid?.projectName || '',
        totalAmount: editBid?.totalAmount?.toString() || '',
        laborAmount: editBid?.laborAmount?.toString() || '',
        equipmentRental: editBid?.equipmentRental?.toString() || '',
        suppliesMaterials: editBid?.suppliesMaterials?.toString() || '',
        projectStartDate: editBid?.projectStartDate ? toDateInputValue(editBid?.projectStartDate) : '',
        projectEndDate: editBid?.projectEndDate ? toDateInputValue(editBid?.projectEndDate) : '',
        projectLocation: editBid?.projectLocation || '',
        description: editBid?.description || '',
        documentationLink: editBid?.documentationLink || '',
        status: editBid?.status || 'Draft'
      });
      // Load existing contractor/vendor associations
      if (editBid?.id) {
        getBidContractors(editBid?.id)?.then(data => {
          setSelectedSCIds(data?.subContractors?.map(c => c?.id) || []);
          setSelectedVendorIds(data?.vendors?.map(v => v?.id) || []);
        })?.catch(err => console.error('Error loading bid contractors:', err?.message));
      }
    } else {
      setFormData({
        customerName: '',
        projectName: '',
        totalAmount: '',
        laborAmount: '',
        equipmentRental: '',
        suppliesMaterials: '',
        projectStartDate: '',
        projectEndDate: '',
        projectLocation: '',
        description: '',
        documentationLink: '',
        status: 'Draft'
      });
      setSelectedSCIds([]);
      setSelectedVendorIds([]);
    }
    setErrors({});
    setSubmitError(null);
  }, [editBid, isOpen]);

  // Calculate breakdown sum
  const breakdownSum = (parseFloat(formData?.laborAmount) || 0) +
    (parseFloat(formData?.equipmentRental) || 0) +
    (parseFloat(formData?.suppliesMaterials) || 0);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    setSubmitError(null);
    // Clear breakdown mismatch error when any relevant field changes
    if (['totalAmount', 'laborAmount', 'equipmentRental', 'suppliesMaterials']?.includes(field)) {
      setErrors(prev => ({ ...prev, breakdownMismatch: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.customerName) newErrors.customerName = 'Customer is required';
    if (!formData?.projectName) newErrors.projectName = 'Project name is required';
    if (!formData?.totalAmount || parseFloat(formData?.totalAmount) <= 0) {
      newErrors.totalAmount = 'Total project amount is required';
    }

    // Breakdown validation: if breakdown sum is not 0, it must equal total amount
    const total = parseFloat(formData?.totalAmount) || 0;
    const currentBreakdownSum = (parseFloat(formData?.laborAmount) || 0) +
      (parseFloat(formData?.equipmentRental) || 0) +
      (parseFloat(formData?.suppliesMaterials) || 0);

    if (currentBreakdownSum !== 0 && Math.abs(currentBreakdownSum - total) > 0.01) {
      newErrors.breakdownMismatch = `Labor + Equipment + Supplies ($${currentBreakdownSum?.toFixed(2)}) must equal the Total Project Amount ($${total?.toFixed(2)}) or all three must be $0.00`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await onSubmit({
        ...formData,
        totalAmount: parseFloat(formData?.totalAmount) || 0,
        laborAmount: parseFloat(formData?.laborAmount) || 0,
        equipmentRental: parseFloat(formData?.equipmentRental) || 0,
        suppliesMaterials: parseFloat(formData?.suppliesMaterials) || 0,
      });
      // Save contractor/vendor associations if we have a bid ID
      const bidId = result?.id || editBid?.id;
      if (bidId) {
        await setBidContractors(bidId, {
          subContractorIds: selectedSCIds,
          vendorIds: selectedVendorIds,
        });
      }
    } catch (err) {
      setSubmitError(err?.message || 'Failed to save bid. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCustomerCreated = (newCustomer) => {
    // Add new customer to list and select it
    setCustomers(prev => [...prev, newCustomer]);
    handleChange('customerName', newCustomer?.companyName);
  };

  const handleContractorChange = ({ subContractorIds, vendorIds }) => {
    setSelectedSCIds(subContractorIds);
    setSelectedVendorIds(vendorIds);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/50">
        <div className="bg-card rounded-xl shadow-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-2xl font-semibold text-foreground">
              {editBid ? 'Edit Bid' : 'Create New Bid'}
            </h2>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={24} />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {submitError && (
                <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
                  <Icon name="AlertCircle" size={16} className="shrink-0" />
                  <span>{submitError}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Select
                    label="Customer"
                    required
                    options={customerOptions}
                    value={formData?.customerName}
                    onChange={(value) => handleChange('customerName', value)}
                    error={errors?.customerName}
                    searchable
                  />
                  <button
                    type="button"
                    onClick={() => setIsQuickCreateOpen(true)}
                    className="flex items-center gap-1 text-xs text-accent hover:text-accent/80 transition-colors mt-1"
                  >
                    <Icon name="UserPlus" size={12} />
                    <span>Create New Customer</span>
                  </button>
                </div>

                <Input
                  label="Project Name"
                  type="text"
                  required
                  value={formData?.projectName}
                  onChange={(e) => handleChange('projectName', e?.target?.value)}
                  error={errors?.projectName}
                  placeholder="Enter project name"
                />
              </div>

              {/* Financial Breakdown */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                  Financial Breakdown
                </h3>

                {/* Total Project Amount — directly editable */}
                <Input
                  label="Total Project Amount"
                  type="number"
                  required
                  value={formData?.totalAmount}
                  onChange={(e) => handleChange('totalAmount', e?.target?.value)}
                  error={errors?.totalAmount}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />

                {/* Always-visible breakdown fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="Labor"
                    type="number"
                    value={formData?.laborAmount}
                    onChange={(e) => handleChange('laborAmount', e?.target?.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                  <Input
                    label="Equipment"
                    type="number"
                    value={formData?.equipmentRental}
                    onChange={(e) => handleChange('equipmentRental', e?.target?.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                  <Input
                    label="Supplies"
                    type="number"
                    value={formData?.suppliesMaterials}
                    onChange={(e) => handleChange('suppliesMaterials', e?.target?.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>

                {/* Breakdown mismatch error */}
                {errors?.breakdownMismatch && (
                  <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
                    <Icon name="AlertCircle" size={16} className="mt-0.5 shrink-0" />
                    <span>{errors?.breakdownMismatch}</span>
                  </div>
                )}
              </div>

              {/* Project Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Project Start Date"
                  type="date"
                  value={formData?.projectStartDate}
                  onChange={(e) => handleChange('projectStartDate', e?.target?.value)}
                  error={errors?.projectStartDate}
                />
                <Input
                  label="Project End Date"
                  type="date"
                  value={formData?.projectEndDate}
                  onChange={(e) => handleChange('projectEndDate', e?.target?.value)}
                  error={errors?.projectEndDate}
                />
              </div>

              <Input
                label="Project Location"
                type="text"
                value={formData?.projectLocation}
                onChange={(e) => handleChange('projectLocation', e?.target?.value)}
                error={errors?.projectLocation}
                placeholder="Enter project location"
              />

              <Input
                label="Description"
                type="text"
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                error={errors?.description}
                placeholder="Enter project description"
              />

              <Input
                label="Documentation Link"
                type="url"
                value={formData?.documentationLink}
                onChange={(e) => handleChange('documentationLink', e?.target?.value)}
                placeholder="https://example.com/documents"
                description="Optional: Link to project documentation"
              />

              <Select
                label="Status"
                required
                options={[
                  { value: 'Draft', label: 'Draft' },
                  { value: 'Submitted', label: 'Submitted' },
                  { value: 'Accepted', label: 'Accepted' },
                  { value: 'Rejected', label: 'Rejected' },
                  { value: 'Converted to Project', label: 'Converted to Project' }
                ]}
                value={formData?.status}
                onChange={(value) => handleChange('status', value)}
              />

              {/* Contractors & Vendors */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Contractors &amp; Vendors
                </label>
                <ContractorsVendorsSelector
                  selectedSCIds={selectedSCIds}
                  selectedVendorIds={selectedVendorIds}
                  onChange={handleContractorChange}
                />
              </div>
            </div>
          </form>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSubmit}
              disabled={submitting}
              iconName={submitting ? undefined : "Save"}
              iconPosition="left"
              iconSize={16}
            >
              {submitting ? 'Saving...' : (editBid ? 'Update Bid' : 'Create Bid')}
            </Button>
          </div>
        </div>
      </div>

      <QuickCreateCustomerModal
        isOpen={isQuickCreateOpen}
        onClose={() => setIsQuickCreateOpen(false)}
        onCreated={handleCustomerCreated}
      />
    </>
  );
};

export default BidFormModal;